import axios from "axios"

import type { DehydratedState, VueQueryPluginOptions } from "@tanstack/vue-query"
import { MutationCache, QueryClient, VueQueryPlugin, dehydrate, hydrate } from "@tanstack/vue-query"

import { useState } from "#app"
import { captureMutationFailure, captureMutationSuccess } from "~/composables/analytics"

export const defaultRetry = (failureCount: number, error: any): boolean => {
  if (error?.name === "ZodError") return false
  if (axios.isAxiosError(error) && [404, 500].includes(error.response?.status as any)) return false
  return failureCount < 3
}

// Export queryClient instance for use outside of setup context (e.g., in middleware)
export const queryClient = new QueryClient({
  // Product analytics: mutations opt in with `trackMutation()` and the outcome is captured here,
  // once the request settled, instead of in every component triggering the mutation.
  mutationCache: new MutationCache({
    onSuccess: (_data, variables, _context, mutation) => {
      captureMutationSuccess(variables, mutation)
    },
    onError: (error, variables, _context, mutation) => {
      captureMutationFailure(error, variables, mutation)
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: defaultRetry,
    },
  },
})

export default defineNuxtPlugin((nuxt) => {
  const vueQueryState = useState<DehydratedState | null>("vue-query")
  const options: VueQueryPluginOptions = { queryClient }

  nuxt.vueApp.use(VueQueryPlugin, options)

  if (import.meta.server) {
    nuxt.hooks.hook("app:rendered", () => {
      vueQueryState.value = dehydrate(queryClient)
    })
  }

  if (import.meta.client) {
    nuxt.hooks.hook("app:created", () => {
      hydrate(queryClient, vueQueryState.value)
    })
  }
})
